import { QueryKeys } from "@app/constants/query-keys.ts";
import { useApis } from "@app/data/apis.ts";
import {
    ActiveEnums,
    StaffContactPostModel,
    StaffNotificationPostModel,
    StaffPerformancePostModel,
    StaffPersonalPostModel,
    StaffProfilePostModel,
    StaffRatePostModel,
} from "@app/data/engine.ts";
import { handleApiError } from "@app/helpers/error-helper.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "bi-lib";
import { toast } from "react-toastify";

interface Props {
    id?: number;
    roleId?: string;
    activeState?: ActiveEnums;
    hourlyState?: ActiveEnums;
    utilId?: number;
    staffIds?: string;
}

export default function useStaffData({ id, roleId, activeState, hourlyState, utilId, staffIds }: Props = {}) {
    const apis = useApis();
    const queryClient = useQueryClient();

    const getStaffQuery = useQuery({
        queryKey: [QueryKeys.Staff, { id }],
        queryFn: () => apis.engine.staff.staffGetStaffList({ id }),
        enabled: id != undefined,
    });

    const getStaffGridQuery = useQuery({
        queryKey: [QueryKeys.StaffGrid, { roleId, activeState, hourlyState }],
        queryFn: () =>
            apis.engine.staff.staffGetStaffGridList({
                roleId: roleId,
                activeState,
                hourlyState,
            }),
    });

    const getStaffingListQuery = useQuery({
        queryKey: [QueryKeys.StaffingList, { activeState }],
        queryFn: () => apis.engine.staff.staffGetStaffingListList({ activeState }),
    });

    const getStaffCompanyListQuery = useQuery({
        queryKey: [QueryKeys.CompanyList],
        queryFn: () => apis.engine.staff.staffGetStaffCompanyListList(),
    });

    const getStaffProfileListQuery = useQuery({
        queryKey: [QueryKeys.StaffProfileList, { id, utilId }],
        queryFn: () => apis.engine.staff.staffGetStaffProfileListList({ id, utilId }),
        enabled: id != undefined && utilId != undefined,
    });

    const getStaffRatesListQuery = useQuery({
        queryKey: [QueryKeys.StaffRatesList, { id }],
        queryFn: () => apis.engine.staff.staffGetStaffRatesList({ id }),
        enabled: id != undefined,
    });

    const getStaffPerformanceListQuery = useQuery({
        queryKey: [QueryKeys.StaffPerformanceList, { id }],
        queryFn: () => apis.engine.staff.staffGetPerformanceListList({ id }),
        enabled: id != undefined,
    });

    const getStaffAssignmentsListQuery = useQuery({
        queryKey: [QueryKeys.StaffAssignmentsList, { staffIds }],
        queryFn: () =>
            apis.engine.staff.staffGetStaffAssignmentsListList({
                id: staffIds,
                showOnlyActive: true,
            }),
        enabled: staffIds != undefined,
    });

    const deleteStaffMemberMutation = useMutation({
        mutationFn: (id: number) => apis.engine.staff.staffDeleteEntryDelete({ id }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.Staff, { id }] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.StaffGrid] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.StaffingList] });
            toast.success("Staff member deleted");
        },
        onError: (error: Error) => handleApiError(error, "Failed to delete staff member"),
    });

    const restoreStaffMemberMutation = useMutation({
        mutationFn: (id: number) => apis.engine.staff.staffRestoreCreate(id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.Staff, { id }] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.StaffGrid] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.StaffingList] });
            toast.success("Staff member restored");
        },
        onError: (error: Error) => handleApiError(error, "Failed to restore staff member"),
    });

    const saveContactMutation = useMutation({
        mutationFn: (post: StaffContactPostModel) => apis.engine.staff.staffContactSaveCreate(post),
        onSuccess: (_, { staffingId }) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.Staff, { id: staffingId }] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.StaffGrid] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.StaffingList] });
            toast.success("Staff member contact info saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save staff member contact info"),
    });

    const saveNotificationMutation = useMutation({
        mutationFn: (post: StaffNotificationPostModel) => apis.engine.staff.staffNotificationSaveCreate(post),
        onSuccess: (_, { staffingId }) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.Staff, { id: staffingId }] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.StaffGrid] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.StaffingList] });
            toast.success("Notification settings saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save notification settings"),
    });

    const saveProfileMutation = useMutation({
        mutationFn: (post: StaffProfilePostModel) => apis.engine.staff.staffProfileSaveCreate(post),
        onSuccess: (_, { staffingId }) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.StaffProfileList, { id: staffingId }],
            });
            toast.success("Profile saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save profile"),
    });

    const savePersonalMutation = useMutation({
        mutationFn: (post: StaffPersonalPostModel) => apis.engine.staff.staffPersonalSaveCreate(post),
        onSuccess: (_, { staffingId }) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.Staff, { id: staffingId }],
            });
            toast.success("Profile personal saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save profile personal"),
    });

    const deleteRateMutation = useMutation({
        mutationFn: (id: number) => apis.engine.staff.staffRateDeleteDelete({ id }),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.StaffRatesList],
            });
            toast.success("Staff rate deleted");
        },
        onError: (error: Error) => handleApiError(error, "Failed to delete staff rate"),
    });

    const saveRateMutation = useMutation({
        mutationFn: (post: StaffRatePostModel) => apis.engine.staff.staffRateSaveCreate(post),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.StaffRatesList],
            });
            toast.success("Staff rate saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save staff rate"),
    });

    const deletePerformanceMutation = useMutation({
        mutationFn: (id: number) => apis.engine.staff.staffPerformanceDeleteDelete({ id }),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.StaffPerformanceList],
            });
            toast.success("Performance deleted");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save performance"),
    });

    const savePerformanceMutation = useMutation({
        mutationFn: (post: StaffPerformancePostModel) => apis.engine.staff.staffPerformanceSaveCreate(post),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.StaffPerformanceList],
            });
            toast.success("Performance saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save performance"),
    });

    return {
        staff: getStaffQuery,
        staffGrid: getStaffGridQuery,
        staffingList: getStaffingListQuery,
        companyList: getStaffCompanyListQuery,
        profileList: getStaffProfileListQuery,
        ratesList: getStaffRatesListQuery,
        staffPerformance: getStaffPerformanceListQuery,
        staffAssignmentsList: getStaffAssignmentsListQuery,

        deleteStaffMember: deleteStaffMemberMutation,
        restoreStaffMember: restoreStaffMemberMutation,
        saveContact: saveContactMutation,
        saveNotification: saveNotificationMutation,
        saveProfile: saveProfileMutation,
        savePersonal: savePersonalMutation,
        deleteRate: deleteRateMutation,
        saveRate: saveRateMutation,
        deletePerformance: deletePerformanceMutation,
        savePerformance: savePerformanceMutation,
    };
}
